/**
 * A linkage template as the operator is editing it, and every edit as a pure
 * function (Feature 71, module 4).
 *
 * The canvas draws `toFlow(draft)`, and each gesture becomes one of the edits
 * below, so what the canvas does is tested without drawing one. Node ids are
 * `first` and the step names; edge ids are `from→to`: the same names the agent
 * uses for a problem's `where`, so a refused save lands on its node or edge.
 *
 * The agent judges a save whole. These edits refuse only what would make the
 * canvas itself wrong (a link into `first`, a loop, a duplicate); everything
 * else is the server's to report.
 *
 * A decision (Feature 79) is a node too: it follows the one node its `from`
 * names, and its way in is that `from`, not a link. A link out of it follows
 * a branch and has no gate; the draft keeps an empty rule set on it so every
 * reader of a link's rules stays as it is, and a save leaves it out. A
 * decision's own edits are `decisions.ts`'s. The console draws it as a gate
 * node, each branch as a condition node of its own (revision 1, D26), whose
 * position the layout keeps under `<gate>.<branch>` (D32).
 *
 * Renaming a step is `rename.ts`'s, and arranging the canvas `layout.ts`'s.
 */
import type {
  LayoutPoint,
  LinkageDecision,
  LinkageGateOutcome,
  LinkageJoin,
  LinkageLink,
  LinkageListCount,
  LinkageMode,
  LinkageProblem,
  LinkageRules,
  LinkageSource,
  LinkageStart,
  LinkageStep,
  LinkageTemplate,
  LinkageTemplateBody,
  LinkageWrite,
} from '@optum/api-client/types';

import { conditionId, conditionsOf, parseConditionId } from './conditions.js';
import type { Gate } from './gates.js';
import { besideGate, layered } from './layout.js';
import { COMPLETION, isCall } from './start.js';

export const FIRST = 'first';

/** How wide a node is drawn, on the editor's canvas and on a run's. */
export const NODE_WIDTH = 184;

/** A link as the draft holds it: always a rule set and an auto pass, empty on a branch. */
export type DraftLink = LinkageLink & { rules: LinkageRules; auto_pass: boolean };

export interface Draft {
  key: string;
  name: string;
  description: string | null;
  /** A completion's capability; empty on a schedule. */
  firstCapabilityId: string;
  start: LinkageStart;
  steps: LinkageStep[];
  links: DraftLink[];
  /** The decisions, each after the node its `from` names (Feature 79). */
  decisions: LinkageDecision[];
  layout: Record<string, LayoutPoint>;
}

/** A node of the canvas: `first` or a step. */
export interface FlowNode {
  id: string;
  position: LayoutPoint;
  data: {
    name: string;
    capabilityId: string;
    first: boolean;
    /** Shown when more than one link comes in. */
    merge: LinkageJoin | null;
    /** On a schedule's `first`: its cron, and its reading in the viewer's timezone. */
    schedule?: { cron: string; reading: string | null } | undefined;
    /** On a call's `first`: the request, drawn with the fork mark (Feature 78 D8). */
    call?: boolean | undefined;
    problems: LinkageProblem[];
    mode: LinkageMode;
    /** A write-back step's target. */
    target: string | null;
  };
}

/** A write-back drawn back to its target: not a link, and never drawn by hand. */
export interface ReturnEdge {
  id: string;
  source: string;
  target: string;
  /** `floor`: the canvas height below every node, where the arrow runs across. */
  data: { writes: LinkageWrite[]; floor: number };
}

/** An edge of the canvas: one link. */
export interface FlowEdge {
  id: string;
  source: string;
  target: string;
  data: {
    rules: LinkageRules;
    problems: LinkageProblem[];
    gate: Gate;
    /** On a run's canvas, what the gate decided once its step was decided. */
    outcome?: LinkageGateOutcome | undefined;
    /** On a run's canvas, what each of its list tests counted (Feature 77 D24). */
    lists?: LinkageListCount[] | undefined;
    /** A link leaving a call's `first`: one of its branches (Feature 78 D8). */
    branch?: boolean | undefined;
  };
}

/** Why a connection is refused, or null when it may be drawn. */
export type ConnectRefusal =
  | 'into_first'
  | 'into_decision'
  | 'into_condition'
  | 'self'
  | 'duplicate'
  | 'loop'
  | 'unknown';

function emptyRules(): LinkageRules {
  return { join: 'all', items: [] };
}

/**
 * Links as the draft holds them: a gate saved before Feature 72 does not
 * pass automatically (D9), and a link out of a decision has an empty rule set.
 */
export function draftLinks(links: readonly LinkageLink[]): DraftLink[] {
  return links.map((l) => ({
    ...l,
    rules: l.rules ?? emptyRules(),
    auto_pass: l.auto_pass ?? false,
  }));
}

/** A link as a save writes it: a branch's without a gate (Feature 79 D9). */
function wireLink(link: DraftLink): LinkageLink {
  return link.branch === undefined ? link : { from: link.from, branch: link.branch, to: link.to };
}

/** Every link, and each decision's way in from the node it follows. */
function edgesOf(draft: Draft): { from: string; to: string }[] {
  return [...draft.links, ...draft.decisions.map((d) => ({ from: d.from, to: d.name }))];
}

/** The ancestors of a node: every node with a path to it, through decisions too. */
export function upstream(draft: Draft, name: string): Set<string> {
  const parents = new Map<string, string[]>();
  for (const link of edgesOf(draft)) {
    parents.set(link.to, [...(parents.get(link.to) ?? []), link.from]);
  }
  const found = new Set<string>();
  const pending = [...(parents.get(name) ?? [])];
  while (pending.length > 0) {
    const node = pending.pop() as string;
    if (found.has(node)) continue;
    found.add(node);
    pending.push(...(parents.get(node) ?? []));
  }
  return found;
}

/** The `from→to` id of a link, the agent's own name for it. */
export function linkId(from: string, to: string): string {
  return `${from}→${to}`;
}

export function emptyDraft(): Draft {
  return {
    key: '',
    name: '',
    description: null,
    firstCapabilityId: '',
    start: COMPLETION,
    steps: [],
    links: [],
    decisions: [],
    layout: {},
  };
}

export function fromTemplate(template: LinkageTemplate): Draft {
  return {
    key: template.key,
    name: template.name,
    description: template.description,
    firstCapabilityId: template.first_capability_id ?? '',
    // A template saved before Feature 77 starts on its capability's completion.
    start: template.start ?? COMPLETION,
    // A step saved before modes existed is merged (Feature 71 D39).
    steps: template.graph.steps.map((s) => ({ ...s, mode: s.mode ?? 'merged' })),
    links: draftLinks(template.graph.links),
    decisions: template.graph.decisions ?? [],
    layout: template.layout,
  };
}

/** The body a save sends, with every node's position as the canvas shows it. */
export function toBody(draft: Draft): LinkageTemplateBody {
  return {
    key: draft.key,
    name: draft.name.trim(),
    description: draft.description,
    // Every start names its first capability (Feature 80 D14).
    first_capability_id: draft.firstCapabilityId,
    start: draft.start,
    graph: {
      steps: draft.steps,
      links: draft.links.map(wireLink),
      // Written only when there are any, as the agent writes them back (Feature 79).
      ...(draft.decisions.length > 0 ? { decisions: draft.decisions } : {}),
    },
    layout: positions(draft),
  };
}

/** A step name made from a capability id, unique in the draft: `bant_analyzer_v1` → `bant_analyzer`. */
export function slugFor(capabilityId: string, taken: Iterable<string>): string {
  const used = new Set(taken);
  used.add(FIRST);
  let base = capabilityId
    .toLowerCase()
    .replace(/_v\d+$/, '')
    .replace(/[^a-z0-9_]+/g, '_')
    .replace(/^_+|_+$/g, '');
  if (!/^[a-z]/.test(base)) base = `step_${base}`;
  base = base.slice(0, 60) || 'step';
  if (!used.has(base)) return base;
  let n = 2;
  while (used.has(`${base}_${n}`)) n += 1;
  return `${base}_${n}`;
}

export function addStep(
  draft: Draft,
  capabilityId: string,
  at?: LayoutPoint | undefined,
): { draft: Draft; name: string } {
  const name = slugFor(
    capabilityId,
    draft.steps.map((s) => s.name),
  );
  const step: LinkageStep = {
    name,
    capability_id: capabilityId,
    merge: 'all',
    mapping: {},
    mode: 'merged',
  };
  const layout = at ? { ...draft.layout, [name]: at } : draft.layout;
  return { draft: { ...draft, steps: [...draft.steps, step], layout }, name };
}

/** A step, every link that touches it, and every decision that follows it with its links. */
export function removeStep(draft: Draft, name: string): Draft {
  const gone = new Set([
    name,
    ...draft.decisions.filter((d) => d.from === name).map((d) => d.name),
  ]);
  // A gate's condition nodes go with it (Feature 79 D32).
  const layout = Object.fromEntries(
    Object.entries(draft.layout).filter(([key]) => !gone.has(parseConditionId(key)?.gate ?? key)),
  );
  return {
    ...draft,
    steps: draft.steps.filter((s) => s.name !== name),
    links: draft.links.filter((l) => !gone.has(l.from) && !gone.has(l.to)),
    decisions: draft.decisions.filter((d) => !gone.has(d.name)),
    layout,
  };
}

export function connectRefusal(draft: Draft, from: string, to: string): ConnectRefusal | null {
  const names = new Set([FIRST, ...draft.steps.map((s) => s.name)]);
  // A decision's way in is its `from`; a link out of it leaves a branch (Feature 79).
  if (draft.decisions.some((d) => d.name === to)) return 'into_decision';
  if (!names.has(from) || !names.has(to)) return 'unknown';
  if (to === FIRST) return 'into_first';
  if (from === to) return 'self';
  if (draft.links.some((l) => l.from === from && l.to === to)) return 'duplicate';
  // A link from a step's own descendant would close a loop.
  if (upstream(draft, from).has(to)) return 'loop';
  return null;
}

/**
 * A link with an empty gate, no rules and no auto pass, or the draft unchanged
 * when refused. The save refuses an empty gate, so the operator fills it in
 * (Feature 72 D3).
 */
export function connect(draft: Draft, from: string, to: string): Draft {
  if (connectRefusal(draft, from, to) !== null) return draft;
  if (from === FIRST && isCall(draft.start)) {
    // After a call the step heads its own subtree (Feature 78 D2), so the
    // canvas makes what the agent accepts; its gate is the operator's to fill,
    // on the request's input (D3). After a schedule a step is as after a
    // completion (Feature 80 D6).
    return {
      ...draft,
      steps: draft.steps.map((s) => {
        if (s.name !== to) return s;
        const { write_back: _dropped, ...rest } = s;
        return { ...rest, mode: 'independent' };
      }),
      links: [...draft.links, { from, to, rules: emptyRules(), auto_pass: false }],
    };
  }
  return {
    ...draft,
    links: [...draft.links, { from, to, rules: emptyRules(), auto_pass: false }],
  };
}

/** Whether a link from `source` leaves the call: from `first`, or a decision that follows it. */
export function leavesCall(draft: Draft, source: string): boolean {
  if (!isCall(draft.start)) return false;
  return source === FIRST || draft.decisions.some((d) => d.name === source && d.from === FIRST);
}

/**
 * Whether a step follows a call's `first`, directly or through a decision, and
 * so must be independent (Feature 78 D2, Feature 79 D18).
 */
export function followsCall(draft: Draft, name: string): boolean {
  return draft.links.some((l) => l.to === name && leavesCall(draft, l.from));
}

/** A gate's Pass automatically, on or off; its rules are kept either way (D8). */
export function setAutoPass(draft: Draft, from: string, to: string, on: boolean): Draft {
  return {
    ...draft,
    links: draft.links.map((l) => (l.from === from && l.to === to ? { ...l, auto_pass: on } : l)),
  };
}

export function disconnect(draft: Draft, from: string, to: string): Draft {
  return { ...draft, links: draft.links.filter((l) => !(l.from === from && l.to === to)) };
}

function withStep(draft: Draft, name: string, change: (step: LinkageStep) => LinkageStep): Draft {
  return { ...draft, steps: draft.steps.map((s) => (s.name === name ? change(s) : s)) };
}

export function setMerge(draft: Draft, name: string, merge: LinkageJoin): Draft {
  return withStep(draft, name, (s) => ({ ...s, merge }));
}

/** Another capability for a step; its mapping was for the old one's fields, so it goes. */
export function setCapability(draft: Draft, name: string, capabilityId: string): Draft {
  return withStep(draft, name, (s) =>
    s.capability_id === capabilityId ? s : { ...s, capability_id: capabilityId, mapping: {} },
  );
}

/** One input field's source, or none when `source` is null. */
export function setSource(
  draft: Draft,
  name: string,
  field: string,
  source: LinkageSource | null,
): Draft {
  return withStep(draft, name, (s) => {
    const mapping = { ...s.mapping };
    if (source === null) delete mapping[field];
    else mapping[field] = source;
    return { ...s, mapping };
  });
}

export function setRules(draft: Draft, from: string, to: string, rules: LinkageRules): Draft {
  return {
    ...draft,
    links: draft.links.map((l) => (l.from === from && l.to === to ? { ...l, rules } : l)),
  };
}

export function moveNode(draft: Draft, id: string, point: LayoutPoint): Draft {
  return { ...draft, layout: { ...draft.layout, [id]: point } };
}

/**
 * The edges the canvas is arranged by: each link, a branch's from its
 * condition node; each gate's way in; and each gate's ties to its condition
 * nodes (Feature 79 D26).
 */
function layoutEdges(draft: Draft): { from: string; to: string }[] {
  return [
    ...draft.links.map((l) =>
      l.branch === undefined ? l : { from: conditionId(l.from, l.branch), to: l.to },
    ),
    ...draft.decisions.flatMap((d) => [
      ...(d.from ? [{ from: d.from, to: d.name }] : []),
      ...conditionsOf(d).map((c) => ({ from: d.name, to: c.id })),
    ]),
  ];
}

/** Every node arranged by depth, gate nodes in narrow columns of their own. */
function arranged(draft: Draft): Record<string, LayoutPoint> {
  const gates = new Set(draft.decisions.map((d) => d.name));
  return layered(nodeNames(draft), layoutEdges(draft), FIRST, gates);
}

/** Every node's position: its own when it has one, its arranged one otherwise. */
export function positions(draft: Draft): Record<string, LayoutPoint> {
  const placed = arranged(draft);
  const result: Record<string, LayoutPoint> = {};
  for (const name of nodeNames(draft)) {
    result[name] = draft.layout[name] ?? (placed[name] as LayoutPoint);
  }
  // A condition node with no place of its own stands beside a gate that has one.
  for (const decision of draft.decisions) {
    const gate = draft.layout[decision.name];
    if (!gate) continue;
    conditionsOf(decision).forEach((c, i, all) => {
      if (!draft.layout[c.id]) result[c.id] = besideGate(gate, i, all.length);
    });
  }
  return result;
}

/** Every node of the canvas: `first`, the steps, the gates, and their condition nodes. */
function nodeNames(draft: Draft): string[] {
  return [
    FIRST,
    ...draft.steps.map((s) => s.name),
    ...draft.decisions.map((d) => d.name),
    ...draft.decisions.flatMap((d) => conditionsOf(d).map((c) => c.id)),
  ];
}

/** Every node moved to its arranged position, for the Arrange button. */
export function arrange(draft: Draft): Draft {
  return { ...draft, layout: arranged(draft) };
}

/** A save's problems by where they sit: a node id, an edge id, or `graph`. */
export function problemsByTarget(problems: LinkageProblem[]): Map<string, LinkageProblem[]> {
  const byTarget = new Map<string, LinkageProblem[]>();
  for (const problem of problems) {
    byTarget.set(problem.where, [...(byTarget.get(problem.where) ?? []), problem]);
  }
  return byTarget;
}

/** Whether two drafts would save differently. */
export function sameDraft(a: Draft, b: Draft): boolean {
  return JSON.stringify(toBody(a)) === JSON.stringify(toBody(b));
}
